import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PlusIcon, MagnifyingGlassIcon, PencilIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { truckingBillAPI } from '../../api/waybillAPI';
import { useCompanyContext } from '../../hooks/useCompanyContext';

const PHP = (n) => new Intl.NumberFormat('en-PH', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n || 0);
const fmt = (d) => d ? new Date(d).toLocaleDateString('en-PH') : '—';

export default function TruckingBillingListPage() {
  const navigate = useNavigate();
  const { accountingClientId, contextKind } = useCompanyContext();
  const [bills, setBills] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  useEffect(() => {
    setLoading(true);
    truckingBillAPI.getAll({ billTypeId: 2 })
      .then((r) => setBills(r.data || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [accountingClientId, contextKind]);

  const filtered = bills.filter((b) => {
    const q = search.toLowerCase();
    return !q
      || b.BillName?.toLowerCase().includes(q)
      || b.VendorDONumber?.toLowerCase().includes(q)
      || b.SOANumber?.toLowerCase().includes(q);
  });

  return (
    <Layout pageTitle="Trucking Billing">
      <div className="space-y-4">
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative w-72">
            <MagnifyingGlassIcon className="absolute left-3 top-2.5 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search bill no., SOA no., company…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm w-full focus:outline-none focus:ring-2 focus:ring-teal-500"
            />
          </div>
          <button
            onClick={() => navigate('/billing/trucking-billing/add')}
            className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white text-sm rounded-lg hover:bg-teal-700"
          >
            <PlusIcon className="h-4 w-4" />
            New Trucking Bill
          </button>
        </div>

        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                {['Bill No.', 'Trucking Company', 'Bill Date', 'Due Date', 'SOA No.', 'Received', 'Actions'].map((h) => (
                  <th key={h} className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {loading ? (
                Array.from({ length: 4 }).map((_, i) => (
                  <tr key={i}>
                    {Array.from({ length: 7 }).map((_, j) => (
                      <td key={j} className="px-4 py-3"><div className="h-4 bg-gray-200 rounded animate-pulse" /></td>
                    ))}
                  </tr>
                ))
              ) : filtered.length === 0 ? (
                <tr>
                  <td colSpan={7} className="px-4 py-10 text-center text-gray-400">
                    No trucking bills found.{' '}
                    <button onClick={() => navigate('/billing/trucking-billing/add')} className="text-teal-600 underline">
                      Create one.
                    </button>
                  </td>
                </tr>
              ) : (
                filtered.map((b) => (
                  <tr key={b._id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 font-medium text-gray-900">{b.BillName}</td>
                    <td className="px-4 py-3 text-gray-600">{b.VendorDONumber || '—'}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{fmt(b.BillDueDate)}</td>
                    <td className="px-4 py-3 text-gray-600">{b.SOANumber || '—'}</td>
                    <td className="px-4 py-3">
                      {b.isVoided ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-red-100 text-red-600 line-through">Voided</span>
                      ) : b.IsReceived ? (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-green-100 text-green-700">Received</span>
                      ) : (
                        <span className="px-2 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-600">Pending</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <button
                        onClick={() => navigate(`/billing/trucking-billing/${b._id}/edit`)}
                        className="p-1.5 rounded hover:bg-teal-50 text-teal-600"
                        title="Edit"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
